import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Nullable } from 'global';
import { UserProfile } from 'types';
import { ContactAssignmentTableEntity } from '../types';
import {
  GetLenderProfileAssignmentTitlesResponse,
  GetLenderProfileAssignmentTitlesVariables,
  UpsertLenderProfileAssignmentTitlesResponse,
  UpsertLenderProfileAssignmentTitlesVariables,
} from '../api/types';
import { dealerManagementApi } from '../api';
import {
  createContactAssignmentTableColumns,
  formatSavedContactAssignment,
  getContactAssignmentTableUserOptions,
  getContactsAssignmentsToSet,
} from '../lib';
import { useNotifications } from 'modules/notification';

export const useContactsAssignments = (contacts: UserProfile[], lenderProfileId: string) => {
  const { showNotification } = useNotifications();

  const [contactsAssignments, setContactsAssignments] = useState<ContactAssignmentTableEntity[]>([]);

  const { loading: isDataLoading } = useQuery<
    GetLenderProfileAssignmentTitlesResponse,
    GetLenderProfileAssignmentTitlesVariables
  >(dealerManagementApi.getLenderProfileAssignmentTitles, {
    variables: { lenderProfileId },
    onCompleted(data) {
      const contactsAssignmentsToSet = getContactsAssignmentsToSet(
        data?.lenderProfileAssignmentTitles ?? [],
        lenderProfileId
      );
      setContactsAssignments(contactsAssignmentsToSet);
    },
  });

  const [upsertLenderProfileAssignmentTitles, { loading: isUpsertLoading }] = useMutation<
    UpsertLenderProfileAssignmentTitlesResponse,
    UpsertLenderProfileAssignmentTitlesVariables
  >(dealerManagementApi.upsertLenderProfileAssignmentTitles, {
    onCompleted(data) {
      const savedAssignment = data?.upsertLenderProfileAssignmentTitles?.[0];
      if (!savedAssignment) {
        return;
      }
      setContactsAssignments(prev =>
        prev.map(ca => (ca.title === savedAssignment.title ? formatSavedContactAssignment(savedAssignment) : ca))
      );
      showNotification('Changes Saved!');
    },
    onError() {
      showNotification('Error!', { type: 'error' });
    },
  });

  const onAssignmentChange = (userProfileId: Nullable<string>, assignment: ContactAssignmentTableEntity) => {
    upsertLenderProfileAssignmentTitles({
      variables: {
        input: {
          lenderProfileAssignmentTitles: [
            {
              ...assignment,
              userProfileId,
            },
          ],
        },
      },
    });
  };

  const contactsOptions = getContactAssignmentTableUserOptions(contacts);

  const columns = createContactAssignmentTableColumns(contactsOptions, isUpsertLoading, onAssignmentChange);

  return {
    isLoading: isDataLoading || isUpsertLoading,
    contactsAssignments,
    columns,
  };
};
